import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import AppContext, { OBJECT_SEARCH, SEARCH_ENGINE_SPATIAL } from '../../context/AppContext';
import MapContext from '../../context/MapContext';
import LoginContext from '../../context/LoginContext';
import PoiManager, {
    createPoiCache,
    DEFAULT_ICON_COLOR,
    DEFAULT_POI_COLOR,
    DEFAULT_POI_SHAPE,
    navigateToPoi,
    updatePoiCache,
    parseBrandType,
} from '../../manager/PoiManager';
import { useMap } from 'react-leaflet';
import { getPoiIcon } from './PoiLayer';
import L from 'leaflet';
import {
    BBOX_LAT_LON,
    CATEGORY_NAME,
    CATEGORY_TYPE,
    FINAL_POI_ICON_NAME,
    MATCHED_OBJECTS,
    POI_ID,
    POI_NAME,
    RELATED_RESULT_IDS,
    WEB_VISIBLE_LEVEL,
} from '../../infoblock/components/wpt/WptTagsProvider';
import { changeIconColor, createPoiIcon, DEFAULT_ICON_SIZE } from '../markers/MarkerOptions';
import { clusterMarkers, addMarkerTooltip, createSecondaryMarker } from '../util/Clusterizer';
import { useSelectMarkerOnMap } from '../../util/hooks/map/useSelectMarkerOnMap';
import useZoomMoveMapHandlers from '../../util/hooks/map/useZoomMoveMapHandlers';
import {
    buildFavoriteFeatures,
    buildTrackFeatures,
    buildWptFeatures,
    getIconByType,
    getObjIdSearch,
} from '../../manager/SearchManager';
import { POI_LAYER_ID, SEARCH_LAYER_ID, showProcessingNotification } from '../../manager/GlobalManager';
import { getVisibleBboxInfo } from './MapStateLayer';
import {
    findFeatureGroupById,
    getIconFromMap,
    MAP_VIEW_SEARCH_RESULT,
    panToIfNeeded,
    pushMapView,
} from '../util/MapManager';
import { hideMarkersUnderPin } from '../util/MarkerSelectionService';
import { POI_OBJECTS_KEY, useRecentDataSaver } from '../../util/hooks/menu/useRecentDataSaver';
import { useNavigate } from 'react-router-dom';
import { searchByWordApi, getMapsFromUrl, searchUserDataApi } from '../../manager/SearchApi';
import { fitBoundsOptions } from '../../manager/track/TracksManager';
import { FAVORITE_HIT_GROUP_ID, searchTypeMap, USER_OBJECT_TYPES } from '../../manager/searchConstants';
import {
    getAdditionalMatchedAmenityObjects,
    getMatchedAmenityProperties,
    hasValidMatchedObjectCoords,
} from '../../manager/SpatialSearchMatchedObjects';

export const ZOOM_TO_MAP = 17;

// one channel for both engines: a new query cancels the previous one
const SEARCH_ABORT_KEY = 'searchByWord';

// Build Map<groupId, Set<wptName>> from favorite features for FavoriteLayer visibility control.
export function buildFavGroupMap(favoriteFeatures) {
    if (!favoriteFeatures?.length) return null;
    const result = new Map();
    favoriteFeatures.forEach((f) => {
        const groupId = f.properties[FAVORITE_HIT_GROUP_ID];
        const wptName = f.properties[POI_NAME];
        if (groupId && wptName) {
            if (!result.has(groupId)) result.set(groupId, new Set());
            result.get(groupId).add(wptName);
        }
    });
    return result.size > 0 ? result : null;
}

function getBboxLatLngBounds(bbox) {
    if (!bbox) return null;
    const top = Number(bbox.top);
    const left = Number(bbox.left);
    const bottom = Number(bbox.bottom);
    const right = Number(bbox.right);
    if (![top, left, bottom, right].every(Number.isFinite)) return null;

    return L.latLngBounds([
        [bottom, left],
        [top, right],
    ]);
}

function fitBboxIfValid({ map, mtx, bbox }) {
    const bounds = getBboxLatLngBounds(bbox);
    if (!map || !bounds?.isValid()) {
        return false;
    }

    map.fitBounds(bounds, fitBoundsOptions(mtx));

    return true;
}

export default function SearchLayer() {
    const ctx = useContext(AppContext);
    const mtx = useContext(MapContext);
    const ltx = useContext(LoginContext);
    const map = useMap();

    const navigate = useNavigate();

    const skipFirstBoundsUpdate = useRef(true);

    const [zoom, setZoom] = useState(map ? map.getZoom() : 0);
    const [move, setMove] = useState(false);

    const searchLayers = useRef(null);

    useZoomMoveMapHandlers(map, setZoom, setMove);
    const recentSaver = useRecentDataSaver();

    const getSearchLayers = useCallback(() => searchLayers.current?.getLayers() ?? null, []);
    useSelectMarkerOnMap({
        ctx,
        getLayers: getSearchLayers,
        type: SEARCH_LAYER_ID,
        map,
        zoom,
        move,
    });

    useEffect(() => {
        if (ctx.zoomToCoords) {
            if (!fitBboxIfValid({ map, mtx, bbox: ctx.zoomToCoords.bbox })) {
                panToIfNeeded({ map, latlng: { lat: ctx.zoomToCoords.lat, lon: ctx.zoomToCoords.lon }, ctx });
            }
            ctx.setZoomToCoords(null);
        }
    }, [ctx.zoomToCoords]);

    useEffect(() => {
        let cancelled = false;
        removeOldSearchLayer();
        const oldPoiLayer = findFeatureGroupById(map, POI_LAYER_ID);
        if (oldPoiLayer) {
            map.removeLayer(oldPoiLayer);
        }
        // a category search is finished by PoiLayer, anything else that starts no search must release the spinner
        const startedSearch = ctx.searchQuery?.type || (ctx.searchQuery?.query && ctx.searchQuery?.latlng);
        if (!startedSearch) {
            ctx.setProcessingSearch(false);
        }
        if (ctx.searchQuery?.query || ctx.searchQuery?.type) {
            ctx.setShowPoiCategories([]);
            if (ctx.searchQuery.type) {
                searchByCategory(ctx.searchQuery);
            } else if (ctx.searchQuery.latlng) {
                searchByWord(ctx.searchQuery, () => cancelled).then();
            } else {
                console.debug('SearchLayer: search query without latlng');
            }
        } else if (ctx.configureMapState.pois) {
            ctx.setShowPoiCategories([...ctx.configureMapState.pois]);
        } else {
            ctx.setShowPoiCategories([]);
        }

        return () => {
            cancelled = true;
        };
    }, [ctx.searchQuery]);

    // When tracks or favorites change (rename, edit, delete), refresh their part of open search results
    useEffect(() => {
        const query = ctx.searchQuery?.query;
        if (!query || ctx.searchQuery?.type || !ctx.searchResult) {
            return;
        }
        searchUserData(query).then((userData) => {
            const { features, favoriteGroupIds } = buildUserDataFeatures(userData);
            ctx.setSearchFavoriteGroupIds(favoriteGroupIds);
            ctx.setSearchResult((prev) => {
                if (!prev) return prev;
                const serverFeatures = (prev.features ?? []).filter(
                    (f) => !USER_OBJECT_TYPES.has(f.properties?.[CATEGORY_TYPE])
                );
                return { ...prev, features: [...features, ...serverFeatures] };
            });
        });
    }, [ctx.favorites, ctx.listFiles, ctx.gpxFiles, ctx.shareWithMeFiles?.tracks]);

    useEffect(() => {
        let cancelled = false;
        const updateAsyncLayers = async () => {
            const layers = searchLayers.current;
            if (!layers) {
                return;
            }
            const newLayers = await createSearchLayer({
                objList: filterByVisibleLevel(ctx.searchResult?.features, ctx.spatialSearch, ctx.searchVisibleLevel),
            });
            // a result that arrived meanwhile is drawn by its own layer group - this one is outdated
            if (cancelled || searchLayers.current !== layers) {
                return;
            }
            layers.clearLayers();
            newLayers.eachLayer((l) => {
                layers.addLayer(l);
            });
            hideMarkersUnderPin(map, ctx);
        };

        if (ctx.searchResult?.features && ctx.searchQuery && !ctx.searchQuery.type) {
            updateAsyncLayers().then();
        }
        const newBounds = map.getBounds();
        if (skipFirstBoundsUpdate.current) {
            skipFirstBoundsUpdate.current = false;
        } else if (!ctx.visibleBounds?.equals(newBounds)) {
            ctx.setVisibleBounds(newBounds);
        }

        return () => {
            cancelled = true;
        };
    }, [zoom, move]);

    useEffect(() => {
        if (ctx.moveToMapObj) {
            if (ctx.moveToMapObj.pushMapViewWithCtrlClick) {
                pushMapView({ map, mtx, key: MAP_VIEW_SEARCH_RESULT });
            }
            const [lng, lat] = ctx.moveToMapObj.geometry.coordinates;
            if (!fitBboxIfValid({ map, mtx, bbox: ctx.moveToMapObj.properties?.[BBOX_LAT_LON] })) {
                panToIfNeeded({ map, latlng: { lat, lng }, ctx });
            }
            ctx.setMoveToMapObj(null);
        }
    }, [ctx.moveToMapObj]);

    async function searchByWord(searchData, isCancelled) {
        ctx.setSearchFavoriteGroupIds(null);
        ctx.setSearchResult(null);
        const visible = getVisibleBboxInfo(ctx, map);
        if (!visible) {
            ctx.setProcessingSearch(false);
            return;
        }
        const notifyTimeout = showProcessingNotification(ctx);
        try {
            const result = await fetchSearchResult({ searchData, bbox: visible.bounds });
            if (isCancelled()) {
                return;
            }
            applySearchResult(result);
        } catch (e) {
            if (e?.name !== 'AbortError') throw e;
            // AbortError: search was cancelled by a newer request — ignore silently
        } finally {
            clearTimeout(notifyTimeout);
            if (!isCancelled()) {
                ctx.setProcessingSearch(false);
            }
        }
    }

    // collects everything a word search shows, without touching the context
    async function fetchSearchResult({ searchData, bbox }) {
        const spatialSearch = searchData.engine ? searchData.engine === SEARCH_ENGINE_SPATIAL : ctx.spatialSearch;
        const userDataPromise = searchUserData(searchData.query);
        const response = await searchByWordApi({
            latlng: searchData.latlng,
            bbox,
            query: searchData.query,
            baseSearch: searchData.baseSearch,
            spatial: spatialSearch,
            abortControllerKey: SEARCH_ABORT_KEY,
            maps: getMapsFromUrl(),
        });
        if (response?.aborted) {
            return { aborted: true };
        }
        if (!response?.ok) {
            return { failed: true };
        }
        const data = await response.json();

        return { data, userData: await userDataPromise };
    }

    // the only place where the result of a word search reaches the context
    function applySearchResult(result) {
        if (result.aborted) {
            return;
        }
        ctx.setSearchVisibleLevel(0);
        if (result.failed) {
            ctx.setSearchFavoriteGroupIds(null);
            ctx.setSearchResult(null);
            return;
        }
        const { features, favoriteGroupIds } = buildUserDataFeatures(result.userData);
        ctx.setSearchFavoriteGroupIds(favoriteGroupIds);
        ctx.setSearchResult({ ...result.data, features: [...features, ...(result.data?.features ?? [])] });
    }

    // Server matches, ranks and limits tracks, favorites and waypoints of opened tracks
    async function searchUserData(query) {
        if (!ltx.loginUser) {
            return null;
        }
        const visibleTracksWithWpts = (files, shared) =>
            Object.values(files ?? {})
                .filter((file) => file.url && file.wpts?.length)
                .map((file) => ({ file: file.name, shared }));
        const openedTracks = [
            ...visibleTracksWithWpts(ctx.gpxFiles, false),
            ...visibleTracksWithWpts(ctx.shareWithMeFiles?.tracks, true),
        ];
        const response = await searchUserDataApi({ query, openedTracks });
        return response?.ok ? await response.json() : null;
    }

    // user data features are shown before the server results
    function buildUserDataFeatures(userData) {
        const favorites = buildFavoriteFeatures(ctx.favorites, userData?.favorites ?? []);

        return {
            features: [
                ...buildTrackFeatures(userData?.tracks ?? []),
                ...favorites,
                ...buildWptFeatures(ctx, userData?.wpts ?? []),
            ],
            favoriteGroupIds: buildFavGroupMap(favorites),
        };
    }

    function removeOldSearchLayer() {
        const searchLayer = findFeatureGroupById(map, SEARCH_LAYER_ID);
        if (searchLayer) {
            map.removeLayer(searchLayer);
        }
    }

    useEffect(() => {
        let cancelled = false;
        const addAsyncLayers = async () => {
            if (!ctx.searchResult) {
                removeOldSearchLayer();
                if (ctx.searchQuery?.type) {
                    const category = PoiManager.formattingPoiFilter(ctx.searchQuery?.query, true);
                    removeCategory(category);
                }
            } else {
                if (ctx.searchResult?.features && !ctx.searchQuery?.type) {
                    const layers = await createSearchLayer({
                        objList: filterByVisibleLevel(
                            ctx.searchResult.features,
                            ctx.spatialSearch,
                            ctx.searchVisibleLevel
                        ),
                    });
                    if (cancelled) {
                        return;
                    }
                    removeOldSearchLayer();
                    searchLayers.current = layers;
                    layers.addTo(map).on('click', onClick);
                }
            }
        };

        addAsyncLayers().then();

        return () => {
            cancelled = true;
        };
    }, [ctx.searchResult, ctx.searchVisibleLevel]);

    function onClick(e) {
        ctx.setCurrentObjectType(OBJECT_SEARCH);

        const poi = {
            options: e.sourceTarget.options,
            latlng: e.sourceTarget._latlng,
            mapObj: true,
        };
        recentSaver(POI_OBJECTS_KEY, poi);
        ctx.setSelectedWpt({ poi, id: e.sourceTarget.options?.idObj });
        if (poi.options[CATEGORY_TYPE] === searchTypeMap.POI) {
            navigateToPoi({ poi }, navigate);
        }
    }

    async function createSearchLayer({ objList }) {
        const visibleObjList = filterByVisibleBounds(objList, getVisibleBboxInfo(ctx, map)?.bounds);
        const searchMarkerFeatures = createSearchMarkerFeatures(visibleObjList);
        const innerCache = await createPoiCache({
            poiList: searchMarkerFeatures,
            poiIconCache: ctx.poiIconCache,
        });
        updatePoiCache(ctx, innerCache);

        const center = map.getCenter();
        const zoom = map.getZoom();
        const latitude = center.lat;
        // user objects are rendered by their own layers — skip map markers for them
        const mapMarkerFeatures = searchMarkerFeatures.filter(
            (f) => !USER_OBJECT_TYPES.has(f.properties?.[CATEGORY_TYPE])
        );

        const { mainMarkers, secondaryMarkers } = clusterMarkers({
            places: mapMarkerFeatures,
            zoom,
            latitude,
            isPoi: true,
        });

        const mainMarkersLayers = await Promise.all(
            mainMarkers.map(async (obj) => {
                const objType = obj.properties[CATEGORY_TYPE];
                let title = obj.properties[CATEGORY_NAME];
                let finalIconName = obj.properties[FINAL_POI_ICON_NAME] ?? null;
                let icon;
                if (objType === searchTypeMap.POI) {
                    finalIconName = finalIconName ?? PoiManager.getFinalPoiIconName(obj.properties);
                    icon = await getPoiIcon(obj, innerCache, finalIconName);
                } else {
                    finalIconName = getIconByType(objType);
                    icon = await getSearchIcon(obj, innerCache, finalIconName);
                }
                const coord = obj.geometry.coordinates;
                return new L.Marker(new L.LatLng(coord[1], coord[0]), {
                    ...obj.properties,
                    idObj: getObjIdSearch(obj),
                    title: title,
                    icon,
                    svg: icon.options.svg,
                    [FINAL_POI_ICON_NAME]: finalIconName,
                });
            })
        );

        let simpleMarkersArr = new L.FeatureGroup();

        for (const place of secondaryMarkers) {
            const circle = createSecondaryMarker(place);
            if (circle) {
                simpleMarkersArr.addLayer(circle);
            }
        }

        mainMarkersLayers.forEach((marker) => {
            addMarkerTooltip({
                marker,
                setSelectedId: ctx.setSelectedWptId,
                mainStyle: true,
                text: marker.options[POI_NAME] ?? marker.options[CATEGORY_NAME],
                latlng: marker._latlng,
                iconSize: DEFAULT_ICON_SIZE,
                map,
                ctx,
                type: SEARCH_LAYER_ID,
            });
        });

        simpleMarkersArr.getLayers().forEach((marker) => {
            addMarkerTooltip({
                marker,
                setSelectedId: ctx.setSelectedWptId,
                text: marker.options[POI_NAME] ?? marker.options[CATEGORY_NAME],
                latlng: marker._latlng,
                map,
                ctx,
                type: SEARCH_LAYER_ID,
            });
        });
        const layers = [...mainMarkersLayers, ...simpleMarkersArr.getLayers()];

        if (layers.length) {
            return L.featureGroup(layers, {
                id: SEARCH_LAYER_ID,
            });
        } else {
            return L.featureGroup(); // return an empty layer group if there are no layers
        }
    }

    async function getSearchIcon(obj, cache, finalIconName) {
        let svgData;
        if (cache[finalIconName]) {
            svgData = cache[finalIconName];
        } else {
            svgData = await getIconFromMap(finalIconName);
            if (svgData) {
                ctx.setPoiIconCache((prevState) => ({
                    ...prevState,
                    [finalIconName]: svgData,
                }));
            }
        }
        const coloredSvg = changeIconColor(svgData, DEFAULT_ICON_COLOR);
        const iconHtml = createPoiIcon({
            color: DEFAULT_POI_COLOR,
            background: DEFAULT_POI_SHAPE,
            svgIcon: coloredSvg,
        }).options.html;
        return L.divIcon({ html: iconHtml, svg: coloredSvg });
    }

    function searchByCategory(searchData) {
        let category = searchData.type;
        let lang = searchData.lang;

        const brandInfo = parseBrandType(searchData.type);
        if (brandInfo) {
            category = brandInfo.brandName;
            lang = brandInfo.lang;
        }

        const newCategory = { category, lang };
        ctx.setShowPoiCategories((prev) => [...prev, newCategory]);
    }

    function removeCategory(category) {
        const index = ctx.showPoiCategories.findIndex((item) => item.category === category);
        if (index > -1) {
            ctx.showPoiCategories.splice(index, 1);
        }
        ctx.setShowPoiCategories([...ctx.showPoiCategories]);
    }
}

function filterByVisibleLevel(features, spatialSearch, visibleLevel) {
    if (!spatialSearch) return features;

    return (features ?? []).filter((f) => (f?.properties?.[WEB_VISIBLE_LEVEL] ?? 0) <= visibleLevel);
}

function createSearchMarkerFeatures(features) {
    const markerFeatures = (features ?? [])
        .filter((f) =>
            hasValidMatchedObjectCoords({ lat: f?.geometry?.coordinates?.[1], lon: f?.geometry?.coordinates?.[0] })
        )
        .map((feature) => ({
            ...feature,
            properties: { ...(feature.properties ?? {}) },
        }));

    const featureByKey = new Map();

    markerFeatures.forEach((feature) => {
        const key = getFeatureKey(feature);
        if (key) {
            featureByKey.set(key, feature);
        }
    });

    markerFeatures.forEach((feature) => {
        const resultId = getObjIdSearch(feature);
        getAdditionalMatchedAmenityObjects(feature?.properties?.[MATCHED_OBJECTS]).forEach((obj) => {
            if (!hasValidMatchedObjectCoords(obj)) {
                return;
            }

            const key = getMatchedAmenityKey(obj);
            const existing = featureByKey.get(key);
            if (existing) {
                addRelatedResultId(existing.properties, resultId);
                return;
            }

            const matchedFeature = {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [obj.lon, obj.lat],
                },
                properties: {
                    ...getMatchedAmenityProperties(obj, searchTypeMap.POI),
                },
            };
            addRelatedResultId(matchedFeature.properties, resultId);
            featureByKey.set(key, matchedFeature);
            markerFeatures.push(matchedFeature);
        });
    });

    return markerFeatures;
}

function getFeatureKey(feature) {
    const coord = feature.geometry.coordinates;
    const name = feature.properties?.[POI_NAME] ?? feature.properties?.name ?? feature.properties?.[CATEGORY_NAME];
    return feature?.properties?.[POI_ID] ?? formatSearchMarkerKey(coord[1], coord[0], name);
}

function getMatchedAmenityKey(obj) {
    return obj[POI_ID] ?? formatSearchMarkerKey(obj.lat, obj.lon, obj[POI_NAME] ?? obj.name);
}

function formatSearchMarkerKey(lat, lon, name) {
    return `${lat.toFixed(6)},${lon.toFixed(6)}:${name ?? ''}`;
}

function addRelatedResultId(properties, resultId) {
    if (resultId == null) return;
    const ids = new Set(properties[RELATED_RESULT_IDS] ?? []);
    ids.add(resultId);
    properties[RELATED_RESULT_IDS] = Array.from(ids);
}

function filterByVisibleBounds(features, bounds) {
    if (!bounds) return features ?? [];

    return (features ?? []).filter((f) => {
        const coord = f?.geometry?.coordinates;
        if (!coord) {
            return false;
        }

        return bounds.contains(L.latLng(coord[1], coord[0]));
    });
}
